import { Controller } from '@hotwired/stimulus';
import consumer from '../channels/consumer';
import {
  embedSignature,
  onSignliftEvent,
  finalizeSignature,
  SIGNLIFT_EVENTS,
} from '../lib/signlift';

export default class SignatureChannelController extends Controller {
  static values = {
    subscriptionId: String,
    docReadyTitle: String,
    docReadyImage: String,
    docliftTimeout: { type: Number, default: 180000 },
  };

  static targets = [
    'step2',
    'step3',
    'step4',
    'stepError',
    'contractLoadedButton',
    'inProgressExplanations',
    'readyExplanations',
    'step2Title',
    'step2Image',
    'currentStep',
    'breadcrumbElement',
  ];

  connect() {
    this.iframe = null;
    this.errorDelayTimer = null;
    this.cleanupSignlift = null;
    this.finalizeUrl = null;
    this.finalizeInflight = false;

    this.docliftTimer = setTimeout(
      () => this.handleDocliftTimeout(),
      this.docliftTimeoutValue,
    );

    this.cableSubscription = consumer.subscriptions.create(
      {
        channel: 'SignatureChannel',
        subscription_id: this.subscriptionIdValue,
      },
      {
        received: (data) => this.handleReceived(data),
        rejected: () => this.handleRejected(),
      },
    );
  }

  disconnect() {
    clearTimeout(this.docliftTimer);
    clearTimeout(this.errorDelayTimer);
    this.cableSubscription?.unsubscribe();
    this.cleanupSignlift?.();
  }

  handleDocliftTimeout() {
    if (!this.iframe) {
      consumer.disconnect();
      this.showError();
    }
  }

  handleReceived(data) {
    if (data.status !== 'success') {
      this.showErrorAfterDelay();
      return;
    }

    try {
      clearTimeout(this.docliftTimer);
      this.initIframe(data);
      this.updateStep2ForDocReady();
      this.hideTarget('inProgressExplanations');
      this.showTarget('readyExplanations');

      if (this.hasContractLoadedButtonTarget) {
        this.contractLoadedButtonTarget.disabled = false;
      }
    } catch (error) {
      console.error(
        '[SignatureChannel] Error handling received data:',
        error,
      );
      this.showErrorAfterDelay();
    }
  }

  handleRejected() {
    this.showErrorAfterDelay();
    this.cableSubscription?.unsubscribe();
  }

  advanceToStep3() {
    this.showTarget('step3');
    this.hideTarget('step2');
    this.updateBreadcrumb(3);
    this.element.scrollIntoView({ behavior: 'smooth' });
  }

  // -- Private helpers --

  initIframe(data) {
    if (data.signing_url) {
      this.finalizeUrl = data.finalize_url || null;
      this.generateSignliftIframe(data.signing_url);
    } else if (data.document_url) {
      this.generateContractIframe(data.document_url);
    }
  }

  updateStep2ForDocReady() {
    if (this.hasStep2TitleTarget && this.docReadyTitleValue) {
      this.step2TitleTarget.textContent =
        this.docReadyTitleValue;
    }

    if (this.hasStep2ImageTarget && this.docReadyImageValue) {
      this.step2ImageTarget.src = this.docReadyImageValue;
    }
  }

  generateSignliftIframe(signingUrl) {
    if (!this.hasStep3Target) return;

    this.iframe = embedSignature(this.step3Target, signingUrl);

    this.cleanupSignlift = onSignliftEvent(
      ({ type }) => this.handleSignliftEvent(type),
      signingUrl,
    );
  }

  handleSignliftEvent(type) {
    if (type === SIGNLIFT_EVENTS.SUCCESS) {
      this.advanceToSuccessStep();
      this.triggerFinalize();
    } else if (type === SIGNLIFT_EVENTS.ERROR) {
      this.showError();
    }
  }

  advanceToSuccessStep() {
    this.hideTarget('step3');
    this.showTarget('step4');
    this.updateBreadcrumb(4);
  }

  triggerFinalize() {
    if (!this.finalizeUrl || this.finalizeInflight) return;
    this.finalizeInflight = true;

    finalizeSignature(this.finalizeUrl);
  }

  generateContractIframe(documentUrl) {
    this.iframe = document.createElement('iframe');
    this.iframe.style.width = '100%';
    this.iframe.style.height = '86%';
    this.iframe.src = documentUrl;

    if (this.hasStep3Target) {
      this.step3Target.prepend(this.iframe);
    }

    const editSubscriptionForm = this.element.querySelector(
      '.edit_subscription',
    );
    editSubscriptionForm?.addEventListener(
      'turbo:submit-end',
      (event) => {
        if (!event.detail.success) return;

        this.hideTarget('step3');
        this.showTarget('step4');
        this.updateBreadcrumb(4);
      },
    );
  }

  updateBreadcrumb(step) {
    const prevElement =
      this.breadcrumbElementTargets[step - 2];
    if (prevElement) {
      prevElement.classList.remove('current');
      prevElement.classList.add('past');
    }

    const currentElement =
      this.breadcrumbElementTargets[step - 1];
    if (currentElement) {
      currentElement.classList.remove('next');
      currentElement.classList.add('current');
    }

    if (this.hasCurrentStepTarget) {
      this.currentStepTarget.textContent = String(step);
    }
  }

  showError() {
    this.hideTarget('step2');
    this.hideTarget('step3');
    this.hideTarget('step4');
    this.showTarget('stepError');
  }

  showErrorAfterDelay() {
    this.errorDelayTimer = setTimeout(
      () => this.showError(),
      5000,
    );
  }

  hideTarget(name) {
    const hasKey = `has${name[0].toUpperCase()}${name.slice(1)}Target`;
    if (this[hasKey]) {
      this[`${name}Target`].classList.add('d-none');
    }
  }

  showTarget(name) {
    const hasKey = `has${name[0].toUpperCase()}${name.slice(1)}Target`;
    if (this[hasKey]) {
      this[`${name}Target`].classList.remove('d-none');
    }
  }
}
